import React, { useState, useCallback } from 'react';
import { useForm } from '../context/FormContext';
import { YES_NO_QUESTIONS } from '../steps/yesNoConfig';
import Honeypot from './Honeypot';

import Step01_Company from '../steps/Step01_Company';
import Step02_Personal from '../steps/Step02_Personal';
import Step03_DOB from '../steps/Step03_DOB';
import Step04_SSN from '../steps/Step04_SSN';
import Step05_Address from '../steps/Step05_Address';
import Step06_PreviousAddress from '../steps/Step06_PreviousAddress';
import Step07_Contact from '../steps/Step07_Contact';
import Step08_License from '../steps/Step08_License';
import StepYesNo from '../steps/StepYesNo';
import Step21_JobInstructions from '../steps/Step21_JobInstructions';
import Step22_JobEntries from '../steps/Step22_JobEntries';
import Step23_DOTHours from '../steps/Step23_DOTHours';
import StepSignature from '../steps/StepSignature';
import StepReview from '../steps/StepReview';
import StepSubmission from '../steps/StepSubmission';

// Build the ordered step list based on current form state
function buildSteps(state) {
  const steps = [
    { id: 'company', component: Step01_Company },
    { id: 'personal', component: Step02_Personal },
    { id: 'dob', component: Step03_DOB },
    { id: 'ssn', component: Step04_SSN },
    { id: 'address', component: Step05_Address },
  ];

  // Conditional: previous address if < 3 years at current
  const years = parseFloat(state.currentAddress.years);
  if (!isNaN(years) && years < 3) {
    steps.push({ id: 'previousAddress', component: Step06_PreviousAddress });
  }

  steps.push({ id: 'contact', component: Step07_Contact });
  steps.push({ id: 'license', component: Step08_License });

  // Yes/No questions — q11 (fatality_hazmat) is conditional on accidents_cmv === yes
  YES_NO_QUESTIONS.forEach((q, i) => {
    if (q.conditionalOn) {
      const { key, value } = q.conditionalOn;
      if (state.yesNo[key]?.answer === value) {
        steps.push({ id: `q_${i}`, component: StepYesNo, props: { questionIndex: i } });
      }
    } else {
      steps.push({ id: `q_${i}`, component: StepYesNo, props: { questionIndex: i } });
    }
  });

  steps.push({ id: 'jobInstructions', component: Step21_JobInstructions });
  steps.push({ id: 'jobEntries', component: Step22_JobEntries });
  // Directly before the signature, mirroring the paper short form where the
  // seven-day hours block sits immediately above the signature line — it is
  // the data behind the § 395.8(j)(2) statement the signature authorises.
  steps.push({ id: 'dotHours', component: Step23_DOTHours });
  steps.push({ id: 'signature', component: StepSignature });
  steps.push({ id: 'review', component: StepReview });
  steps.push({ id: 'submission', component: StepSubmission });

  return steps;
}

export default function WizardShell() {
  const { state } = useForm();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [returnToReview, setReturnToReview] = useState(false);

  const steps = buildSteps(state);
  const total = steps.length;
  const progress = Math.round(((currentIndex + 1) / total) * 100);

  // Hide progress bar and nav on submission screen
  const currentStep = steps[currentIndex];
  const isSubmission = currentStep?.id === 'submission';
  const isReview = currentStep?.id === 'review';

  const goNext = useCallback(() => {
    if (returnToReview) {
      setReturnToReview(false);
      const reviewIdx = steps.findIndex(s => s.id === 'review');
      if (reviewIdx >= 0) { setCurrentIndex(reviewIdx); return; }
    }
    setCurrentIndex(i => Math.min(i + 1, total - 1));
    window.scrollTo(0, 0);
  }, [returnToReview, steps, total]);

  const goBack = useCallback(() => {
    setCurrentIndex(i => Math.max(i - 1, 0));
    window.scrollTo(0, 0);
  }, []);

  const goToStep = useCallback((stepId) => {
    const idx = steps.findIndex(s => s.id === stepId);
    if (idx >= 0) {
      setReturnToReview(true);
      setCurrentIndex(idx);
      window.scrollTo(0, 0);
    }
  }, [steps]);

  if (!currentStep) return null;

  const StepComponent = currentStep.component;
  const stepProps = currentStep.props || {};

  return (
    <div>
      <header className="app-header">
        <div className="app-header-logo">MCS</div>
        <div className="app-header-title">Driver Qualification</div>
        {!isSubmission && (
          <>
            <div className="progress-bar-wrap">
              <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="progress-label">{progress}%</div>
          </>
        )}
      </header>

      {/* Bot honeypot (item 31). Rendered on EVERY step, not just the first,
          so a script walking the whole wizard has the entire session to fall
          into it. Invisible and unreachable for a human — see Honeypot.js. */}
      <Honeypot />

      <div className="wizard-body">
        <StepComponent
          onNext={goNext}
          onBack={currentIndex > 0 ? goBack : null}
          onGoToStep={goToStep}
          returnToReview={returnToReview}
          isReview={isReview}
          {...stepProps}
        />
      </div>
    </div>
  );
}
